/* eslint-disable max-len */
import React from 'react';

import { Map, Placemark, SearchControl } from '@pbe/react-yandex-maps';
import { IMark } from '@modules/mark/types';

interface MarksMapProps {
  center?: { lat: number; lng: number };
  marks: IMark[];
  isVisible?: boolean;
  onBoundsChanged: (bounds: { north: number; south: number; east: number; west: number }) => void;
  onMarkerClick: (mark: IMark) => void;
  onMapClick: (lat: number, lng: number) => void;
}

const defaultCenter = {
  lat: 40.1872, // Yerevan
  lng: 44.5152,
};

const MarksMap: React.FC<MarksMapProps> = ({
  marks,
  center = defaultCenter,
  isVisible = true,
  onBoundsChanged,
  onMarkerClick,
  onMapClick,
}) => {
  const mapRef = React.useRef<any>(null);
  const [isMapReady, setIsMapReady] = React.useState(false);

  React.useEffect(() => {
    if (mapRef.current && center) {
      mapRef.current.setCenter([center.lat, center.lng]);
    }
  }, [center]);

  React.useEffect(() => {
    if (mapRef.current && isVisible) {
      mapRef.current.container?.fitToViewport();
    }
  }, [isVisible]);

  return (
    <Map
      instanceRef={(ref) => {
        mapRef.current = ref;
        if (ref && !isMapReady) {
          setIsMapReady(true);
        }
      }}
      defaultState={{ center: [center.lat, center.lng], zoom: 12 }}
      width="100%"
      height="100%"
      onBoundsChange={(e: any) => {
        const bounds = e.get('newBounds');

        if (bounds) {
          onBoundsChanged({
            south: bounds[0][0],
            west: bounds[0][1],
            north: bounds[1][0],
            east: bounds[1][1],
          });
        }
      }}
      onClick={(e: any) => {
        const coords = e.get('coords');

        if (coords) {
          onMapClick(coords[0], coords[1]);
        }
      }}
    >
      <SearchControl
        options={{
          float: 'right',
          noSuggestPanel: true,
          useMapBounds: true,
          boundedBy: [
            [38.82, 43.45],
            [41.30, 46.63],
          ],
          strictBounds: false,
        }}
      />
      {isMapReady &&
        marks
          .filter((m) => {
            const lat = parseFloat(m.latitude as string);
            const lng = parseFloat(m.longitude as string);

            return !Number.isNaN(lat) && !Number.isNaN(lng);
          })
          .map((mark) => (
            <Placemark
              key={mark.id}
              geometry={[parseFloat(mark.latitude as string), parseFloat(mark.longitude as string)]}
              options={{ preset: 'islands#redIcon' }}
              onClick={() => onMarkerClick(mark)}
            />
          ))}
    </Map>
  );
};

export default MarksMap;
